/**
 * SeoAuditsPage — `/seo/audits` (C3). Коммерческие факторы Яндекса: veto-гейты
 * (нет цен/реквизитов/контактов красят скор в 0) + находки → задачи в E1.
 * Находки поставляет AI-слой (скилл seo) или аналитик; здесь — запуск и история.
 */

import { useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { HelpButton } from '@/components/layout/HelpButton';
import { SEO_HELP } from '@/help/seoHelp';
import { Button, LoadingBlock, ErrorBlock, Badge } from '@/shared/ui';
import { useSeoAudits, useSeoMutations, useSeoPhase2Mutations, type SeoAudit } from '@/hooks/api/useSeo';

const VETO_OPTIONS = ['Нет цен / тарифов', 'Нет реквизитов юрлица', 'Нет контактов'];
const SCORE_TONE: Record<string, string> = { red: 'b-danger', yellow: 'b-warning', green: 'b-success' };

export function SeoAuditsPage() {
  usePageHeader({ title: 'SEO — Аудиты', subtitle: 'Коммерческие факторы Яндекса (C3)', actions: <HelpButton help={SEO_HELP.audits} /> });
  const [running, setRunning] = useState(false);
  const audits = useSeoAudits('commercial', running);
  const tech = useSeoAudits('technical', running);
  const { runCommercialAudit } = useSeoMutations();
  const { runTechnicalAudit, runOnpageAudit } = useSeoPhase2Mutations();
  const [url, setUrl] = useState('https://komma.software/');
  const [veto, setVeto] = useState<string[]>([]);
  const [findingsText, setFindingsText] = useState('');
  const [onpageUrl, setOnpageUrl] = useState('https://komma.software/');

  const onStarted = (res: { status?: string } | undefined) => {
    if (res?.status === 'started') {
      setRunning(true);
      setTimeout(() => setRunning(false), 180_000);
    }
  };

  const toggleVeto = (v: string) => setVeto((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const run = () => {
    const findings = findingsText
      .split('\n').map((s) => s.trim()).filter(Boolean)
      .map((title) => ({ title, impact: 'med', effort: 'M', engine: 'yandex', confidence: 'Likely' }));
    runCommercialAudit.mutate({ target_url: url.trim(), veto, findings }, {
      onSuccess: (res) => { setVeto([]); setFindingsText(''); onStarted(res); },
    });
  };

  return (
    <div className="col gap20" style={{ maxWidth: 1050 }}>
      {running && (
        <div className="card" style={{ borderLeft: '3px solid var(--primary)' }}>
          <p style={{ margin: 0, fontSize: 13 }}>AI-аудит запущен (обычно 1–2 мин) — результат и задачи появятся автоматически.</p>
        </div>
      )}
      <section className="card">
        <h3 style={{ marginTop: 0 }}>Запустить коммерческий аудит</h3>
        <input className="input" placeholder="URL страницы" value={url} onChange={(e) => setUrl(e.target.value)} style={{ width: '100%', maxWidth: 460, marginBottom: 10 }} />
        <div className="col" style={{ gap: 6, marginBottom: 10 }}>
          <span className="muted" style={{ fontSize: 12.5, fontWeight: 600 }}>Veto-гейты (отметьте отсутствующее — красит скор в красный):</span>
          {VETO_OPTIONS.map((v) => (
            <label key={v} className="row center" style={{ gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={veto.includes(v)} onChange={() => toggleVeto(v)} />
              {v}
            </label>
          ))}
        </div>
        <textarea className="input" rows={3} placeholder={'Прочие находки (по одной в строке)\nнапр.: Нет кейсов внедрений'} value={findingsText} onChange={(e) => setFindingsText(e.target.value)} style={{ width: '100%', marginBottom: 8 }} />
        <Button variant="primary" size="sm" onClick={run} disabled={runCommercialAudit.isPending || !url.trim()}>
          {runCommercialAudit.isPending ? 'Считаю…' : 'Аудит → задачи'}
        </Button>
        {runCommercialAudit.isError && <ErrorBlock text={runCommercialAudit.error?.message} />}
        <p className="muted" style={{ fontSize: 12, marginBottom: 0, marginTop: 10 }}>
          Полный аудит с находками из живой страницы — через AI-чат: «проверь коммерческие факторы {url}».
        </p>
      </section>

      <section className="card">
        <h3 style={{ marginTop: 0 }}>Технический аудит (C1) и on-page (C2)</h3>
        <div className="row wrap" style={{ gap: 8, marginBottom: 8 }}>
          <Button variant="outline" size="sm" onClick={() => runTechnicalAudit.mutate()} disabled={runTechnicalAudit.isPending}>
            {runTechnicalAudit.isPending ? 'Проверяю…' : 'Тех-аудит (Вебмастер + CWV)'}
          </Button>
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          <input className="input" placeholder="URL для on-page аудита" value={onpageUrl} onChange={(e) => setOnpageUrl(e.target.value)} style={{ minWidth: 260, flex: 1 }} />
          <Button variant="outline" size="sm" onClick={() => runOnpageAudit.mutate(onpageUrl.trim(), { onSuccess: onStarted })} disabled={runOnpageAudit.isPending || !onpageUrl.trim()}>
            {runOnpageAudit.isPending ? 'Запускаю…' : 'On-page аудит → задачи'}
          </Button>
        </div>
        {(runTechnicalAudit.isError || runOnpageAudit.isError) && <ErrorBlock text={(runTechnicalAudit.error ?? runOnpageAudit.error)?.message} />}
        {tech.data && tech.data.audits.length > 0 && (
          <div className="col" style={{ gap: 10, marginTop: 12 }}>
            {tech.data.audits.slice(0, 3).map((a) => <AuditCard key={a.id} a={a} />)}
          </div>
        )}
        <p className="muted" style={{ fontSize: 12, marginBottom: 0, marginTop: 10 }}>
          Индексация Яндекса — по <span className="mono">YANDEX_WEBMASTER_TOKEN</span>; Core Web Vitals — PSI/CrUX; покрытие Google — GSC (Фаза 2, soft-degrade без токенов).
        </p>
      </section>

      {audits.isLoading && <LoadingBlock label="Загружаю аудиты…" />}
      {audits.isError && <ErrorBlock text={audits.error?.message} />}

      {audits.data && (
        audits.data.audits.length === 0 ? (
          <div className="card"><p className="muted" style={{ margin: 0 }}>Аудитов ещё нет.</p></div>
        ) : (
          <section className="card">
            <h3 style={{ marginTop: 0 }}>История коммерческих аудитов</h3>
            <div className="col" style={{ gap: 10 }}>
              {audits.data.audits.map((a) => <AuditCard key={a.id} a={a} />)}
            </div>
          </section>
        )
      )}
    </div>
  );
}

function AuditCard({ a }: { a: SeoAudit }) {
  const color = a.verdict?.color ?? 'yellow';
  return (
    <div className="card" style={{ background: 'var(--surface-2, #F8FAFC)', padding: '10px 12px' }}>
      <div className="row spread center" style={{ gap: 8 }}>
        <span className="mono" style={{ fontSize: 12.5 }}>{a.target_url}</span>
        <span className="row center" style={{ gap: 6 }}>
          <Badge tone={SCORE_TONE[color] ?? 'b-neutral'}>скор {a.score ?? '—'}</Badge>
          {a.verdict?.veto && <Badge tone="b-danger">VETO</Badge>}
        </span>
      </div>
      {a.verdict?.vetoReasons && a.verdict.vetoReasons.length > 0 && (
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>Veto: {a.verdict.vetoReasons.join(' · ')}</div>
      )}
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{new Date(a.created_at).toLocaleString('ru-RU')}</div>
    </div>
  );
}
